import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import type { NewsItem } from "../../../mocks/news";
import { isToday } from "../../../lib/dateHelpers";

interface FeaturedCarouselProps {
  items: NewsItem[];
}

export default function FeaturedCarousel({ items }: FeaturedCarouselProps) {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchDelta = useRef(0);

  const goTo = useCallback((index: number) => {
    if (items.length === 0) return;
    setCurrentIndex((index + items.length) % items.length);
  }, [items.length]);

  const next = useCallback(() => {
    goTo(currentIndex + 1);
  }, [currentIndex, goTo]);

  const prev = useCallback(() => {
    goTo(currentIndex - 1);
  }, [currentIndex, goTo]);

  useEffect(() => {
    if (items.length <= 1 || isPaused) return;
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [items.length, isPaused]);

  // ── Swipe handlers ──
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchDelta.current = 0;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    touchDelta.current = dx;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchStartX.current === null) return;
    const threshold = 50;
    if (touchDelta.current > threshold) {
      prev();
    } else if (touchDelta.current < -threshold) {
      next();
    }
    touchStartX.current = null;
    touchDelta.current = 0;
  }, [next, prev]);

  if (items.length === 0) return null;

  const item = items[currentIndex];
  const articleUrl = `/articulo/${item.slug}`;
  const secondaryBadges = (
    item.secondaryCategories && item.secondaryCategories.length > 0
      ? item.secondaryCategories
      : item.subcategory
        ? [item.subcategory]
        : []
  ).filter(
    (sc) =>
      sc !== item.category &&
      !(sc === "Internacional" && item.isInternational),
  );

  return (
    <article
      className="relative rounded-2xl overflow-hidden group cursor-pointer h-[300px] sm:h-[420px] md:h-[480px] bg-[#0B1F3A] select-none"
      onClick={() => navigate(articleUrl)}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(articleUrl);
        }
        if (e.key === "ArrowRight") next();
        if (e.key === "ArrowLeft") prev();
      }}
    >
      {/* Slides container */}
      <div className="absolute inset-0">
        {items.map((slideItem, index) => (
          <div
            key={slideItem.id}
            className="absolute inset-0 transition-opacity duration-700 ease-in-out"
            style={{ opacity: index === currentIndex ? 1 : 0 }}
          >
            <img
              src={slideItem.image}
              alt={slideItem.title}
              title={slideItem.title}
              className="w-full h-full object-cover object-center"
              onError={(e) => {
                (e.target as HTMLImageElement).src = "https://readdy.ai/api/search-image?query=dramatic%20commercial%20aircraft%20silhouette%20against%20sunset%20sky%20aviation%20photography%20dark%20moody%20cinematic%20wide%20angle&width=800&height=600&seq=fallback-article&orientation=landscape";
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#071529] via-[#071529]/60 to-[#071529]/20" />
          </div>
        ))}
      </div>

      {/* Today badge */}
      {isToday(item.date) && (
        <div className="absolute top-3 left-3 sm:top-4 sm:left-4 z-20 pointer-events-none">
          <span className="inline-flex items-center gap-1 bg-red-500 text-white text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full shadow-sm">
            <i className="ri-flashlight-line text-[10px]" />
            Hoy
          </span>
        </div>
      )}

      {/* International badge */}
      {item.isInternational && (
        <div className={`absolute ${isToday(item.date) ? 'top-8 sm:top-10' : 'top-3 sm:top-4'} left-3 sm:left-4 z-20 pointer-events-none`}>
          <span className="inline-flex items-center gap-1 bg-blue-500/80 text-white text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full shadow-sm border border-blue-400/50 backdrop-blur-sm">
            <i className="ri-earth-line text-[10px]" />
            Internacional
          </span>
        </div>
      )}

      {/* Slide counter */}
      {items.length > 1 && (
        <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20 flex items-center gap-2 pointer-events-none">
          <span className="text-white/70 text-[10px] font-semibold tracking-wider bg-black/30 px-2 py-0.5 rounded-full">
            {currentIndex + 1} / {items.length}
          </span>
        </div>
      )}

      {/* Navigation arrows — siempre visibles en móvil, hover en desktop */}
      {items.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-2 sm:left-4 top-[38%] -translate-y-1/2 z-30 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white backdrop-blur-sm transition-all cursor-pointer opacity-100 md:opacity-0 md:group-hover:opacity-100 border border-white/10"
            aria-label="Noticia anterior"
          >
            <i className="ri-arrow-left-s-line text-base sm:text-xl" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-2 sm:right-4 top-[38%] -translate-y-1/2 z-30 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white backdrop-blur-sm transition-all cursor-pointer opacity-100 md:opacity-0 md:group-hover:opacity-100 border border-white/10"
            aria-label="Noticia siguiente"
          >
            <i className="ri-arrow-right-s-line text-base sm:text-xl" />
          </button>
        </>
      )}

      {/* Bottom content */}
      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-5 md:p-6 z-20 pointer-events-none pb-10 sm:pb-12">
        <div className="flex items-center gap-2 mb-2 sm:mb-3 flex-wrap">
          <span className={`inline-block text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full ${item.categoryColor}`}>
            {item.category}
          </span>
          {secondaryBadges.map((sc) => (
            <span key={sc} className="inline-block text-white/80 text-[9px] font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full border border-white/30">
              {sc}
            </span>
          ))}
        </div>
        <h2
          className="text-white font-bold text-sm sm:text-lg md:text-xl leading-snug mb-2 sm:mb-3 group-hover:text-red-100 transition-colors line-clamp-3"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          {item.title}
        </h2>
        <p
          className="text-slate-300 text-xs sm:text-sm line-clamp-2 mb-3 sm:mb-4 hidden sm:block"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          {item.excerpt}
        </p>
        <div
          className="flex items-center gap-3 text-[11px] sm:text-xs text-slate-400"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          <span>{item.date}</span>
        </div>
      </div>

      {/* Progress dots */}
      {items.length > 1 && (
        <div className="absolute bottom-2 sm:bottom-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 sm:gap-2">
          {items.map((_, index) => (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); goTo(index); }}
              className={`h-1 rounded-full transition-all duration-500 cursor-pointer ${
                index === currentIndex
                  ? "w-5 sm:w-6 bg-white"
                  : "w-1 sm:w-1.5 bg-white/40 hover:bg-white/60"
              }`}
              aria-label={`Ir a noticia ${index + 1}`}
            />
          ))}
        </div>
      )}
    </article>
  );
}